/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Announcements API endpoints.
 *
 * Two surfaces:
 *   - Tenant-facing: /api/v1/announcements/ (read + react)
 *   - Control-facing: /api/v1/control/announcements/ (CRUD + reaction
 *     aggregates)
 *
 * Cache invalidation: posting/updating an announcement invalidates the
 * AdminAnnouncement list (so the TNN admin sees their post immediately)
 * AND the Announcement list (so tenants on the same client session
 * reload). Reacting to an announcement invalidates only that specific
 * Announcement so the my_reaction field updates.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type {
  AdminAnnouncement,
  ReactInput,
  ReactionResponse,
  TenantAnnouncement,
  OrgAnnouncement,
  CreateOrgAnnouncementInput,
} from "../model/types";

interface CreateAnnouncementInput {
  title: string;
  body: string;
  category?: string;
  audience: "all_tenants" | "specific_tenants";
  target_tenants?: number[];
  is_pinned?: boolean;
}

/** RTK Query endpoints for announcements (list, react). */
export const announcementsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * GET /api/v1/announcements/
     *
     * Tenant users see active announcements scoped to their tenant.
     * Backend filters by audience + target_tenants automatically.
     */
    listAnnouncements: build.query<Paginated<TenantAnnouncement>, void>({
      query: () => "/announcements/",
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "Announcement" as const,
                id,
              })),
              { type: "Announcement" as const, id: "LIST" },
            ]
          : [{ type: "Announcement" as const, id: "LIST" }],
    }),

    /**
     * POST /api/v1/announcements/{id}/react/
     *
     * Tenant user reacts to an announcement. Upsert behavior: re-reacting
     * updates the existing row instead of duplicating.
     */
    reactToAnnouncement: build.mutation<
      ReactionResponse,
      { id: number; input: ReactInput }
    >({
      query: ({ id, input }) => ({
        url: `/announcements/${id}/react/`,
        method: "POST",
        body: input,
      }),
      // Reacting changes `my_reaction` on THIS announcement. Both the
      // id tag and the LIST tag resolve to the same (single) list query
      // here, so invalidating the id tag is sufficient to refresh the
      // banner. The LIST tag is kept for forward-safety: if a separate
      // per-announcement detail query is added later, it should also
      // refresh.
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Announcement" as const, id },
        { type: "Announcement" as const, id: "LIST" },
      ],
    }),

    // -------------- Admin endpoints --------------

    /**
     * GET /api/v1/control/announcements/
     *
     * TNN admins see every announcement with reaction breakdowns.
     */
    listAdminAnnouncements: build.query<Paginated<AdminAnnouncement>, void>({
      query: () => "/control/announcements/",
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "AdminAnnouncement" as const,
                id,
              })),
              { type: "AdminAnnouncement" as const, id: "LIST" },
            ]
          : [{ type: "AdminAnnouncement" as const, id: "LIST" }],
    }),

    /**
     * POST /api/v1/control/announcements/
     */
    createAnnouncement: build.mutation<AdminAnnouncement, CreateAnnouncementInput>({
      query: (input) => ({
        url: "/control/announcements/",
        method: "POST",
        body: input,
      }),
      // Invalidate: the admin list (TNN sees their post), the tenant
      // list (tenants on the same session reload), AND the cohort
      // dashboard's ControlTenant LIST - the dashboard surfaces cohort
      // metrics that can be announcement-derived, so it must refetch
      // after a mutation rather than serving a stale cached count
      // (audit V1).
      invalidatesTags: [
        { type: "AdminAnnouncement", id: "LIST" },
        { type: "Announcement", id: "LIST" },
        { type: "ControlTenant", id: "LIST" },
      ],
    }),

    /**
     * PATCH /api/v1/control/announcements/{id}/
     *
     * Used for toggling is_active or editing copy.
     */
    updateAnnouncement: build.mutation<
      AdminAnnouncement,
      { id: number; patch: Partial<CreateAnnouncementInput & { is_active: boolean }> }
    >({
      query: ({ id, patch }) => ({
        url: `/control/announcements/${id}/`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "AdminAnnouncement", id },
        { type: "AdminAnnouncement", id: "LIST" },
        { type: "Announcement", id: "LIST" },
        // Editing or (un)publishing an announcement can change cohort
        // metrics - refresh the dashboard too (audit V1).
        { type: "ControlTenant", id: "LIST" },
      ],
    }),

    /**
     * DELETE /api/v1/control/announcements/{id}/
     */
    deleteAnnouncement: build.mutation<void, number>({
      query: (id) => ({
        url: `/control/announcements/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "AdminAnnouncement", id: "LIST" },
        { type: "Announcement", id: "LIST" },
        { type: "ControlTenant", id: "LIST" },
      ],
    }),

    listOrgAnnouncements: build.query<OrgAnnouncement[], void>({
      query: () => "/org-announcements/",
      // OrgAnnouncementViewSet is router-registered, so DRF paginates it and
      // this returns { count, results }. Without unwrapping, the dashboard's
      // banner calls .map() on that object and takes the whole page down to
      // the error boundary - the exact failure this file's header warns about.
      transformResponse: (r: { results?: OrgAnnouncement[] } | OrgAnnouncement[]) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: [{ type: "OrgAnnouncement", id: "LIST" }],
    }),

    createOrgAnnouncement: build.mutation<OrgAnnouncement, CreateOrgAnnouncementInput>({
      query: (body) => ({
        url: "/org-announcements/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "OrgAnnouncement", id: "LIST" }],
    }),

    dismissOrgAnnouncement: build.mutation<{ dismissed: boolean; id: number }, number>({
      query: (id) => ({
        url: `/org-announcements/${id}/dismiss/`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "OrgAnnouncement", id: "LIST" }],
    }),
  }),
});

export const {
  useListAnnouncementsQuery,
  useReactToAnnouncementMutation,
  useListAdminAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useListOrgAnnouncementsQuery,
  useCreateOrgAnnouncementMutation,
  useDismissOrgAnnouncementMutation,
} = announcementsApi;
